import { Component, OnInit, ViewChild, inject, signal, effect, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { Location } from "@angular/common";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { Store } from "@ngrx/store";

import { filter, distinctUntilChanged } from "rxjs/operators";

import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DrawerModule, Drawer } from "primeng/drawer";
import { SkeletonModule } from "primeng/skeleton";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { environment } from "@env/environment";
import { selectSelectedCompanyId, selectUserLanguage, selectUserHasNoCompanies, selectLoadingUser } from "../store/user/user.selectors";
import { selectSettingsDrawerVisible, selectHasNavigated } from "../store/navigation/navigation.selectors";
import { NavigationActions } from "../store/navigation/navigation.actions";
import { AuthenticationActions } from "../store/application/authentication/authentication.actions";
import { selectIsAuthenticated, selectAuthenticationLoading } from "../store/application/authentication/authentication.selectors";
import { SignInLoaderComponent } from "./sign-in-loader/sign-in-loader.component";
import { SettingsDrawerComponent } from "../navigation/settings-drawer/settings-drawer.component";
import { NavigationHeaderComponent } from "../navigation/navigation-header/navigation-header.component";
import { SideNavigationComponent } from "../navigation/side-navigation/side-navigation.component";
import { BreadcrumbComponent } from "../shared/components/breadcrumb/breadcrumb.component";
import { AccountNoCompaniesComponent } from "../pages/error/account-no-companies/account-no-companies.component";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DrawerModule,
        RouterOutlet,
        TranslateModule,
        SignInLoaderComponent,
        SettingsDrawerComponent,
        NavigationHeaderComponent,
        SideNavigationComponent,
        SkeletonModule,
        BreadcrumbComponent,
        ToastModule,
        ConfirmDialogModule,
        AccountNoCompaniesComponent,
    ],
})
export class AppComponent implements OnInit {
    // === Dependencies ===
    private readonly titleService = inject(Title);
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly location = inject(Location);

    // === View Children ===
    @ViewChild("drawer") public drawer: Drawer;

    // === Store Selectors ===
    public readonly isAuthenticated = toSignal(this.store.select(selectIsAuthenticated), { initialValue: false });
    public readonly authenticationLoading = toSignal(this.store.select(selectAuthenticationLoading), { initialValue: true });
    public readonly userLoading = toSignal(this.store.select(selectLoadingUser), { initialValue: true });
    public readonly userHasNoCompanies = toSignal(this.store.select(selectUserHasNoCompanies), { initialValue: false });
    private readonly selectedCompanyId = toSignal(this.store.select(selectSelectedCompanyId), { initialValue: null });
    private readonly userLanguage = toSignal(
        this.store.select(selectUserLanguage).pipe(
            filter((lang): lang is string => !!lang && lang.length > 0),
            distinctUntilChanged(),
        ),
        { initialValue: null },
    );
    private readonly routeParams = toSignal(this.route.paramMap);

    public readonly settingsDrawerVisible = toSignal(this.store.select(selectSettingsDrawerVisible), { initialValue: false });
    public readonly hasNavigated = toSignal(this.store.select(selectHasNavigated), { initialValue: false });

    // === Local State ===
    public readonly isPrintRoute = signal(false);

    // === Constructor with Effects ===
    constructor() {
        // Effect: Sync URL company ID with selected company
        effect(() => {
            const selectedCompany = this.selectedCompanyId();
            const params = this.routeParams();
            if (!selectedCompany || !params) return;

            const urlCompanyId = params.get("companyId");
            if (urlCompanyId === selectedCompany) return;

            // Use the live browser URL — not router.url, which returns "/" until
            // the initial navigation completes. Reading router.url too early
            // produced a "/" address-bar flash on every deep-link reload.
            const currentPath = this.location.path();
            const newUrl = currentPath.replace(/^\/\d{6}\//, `/${selectedCompany}/`);
            // Skip when nothing changed (e.g. path lacks a 6-digit company prefix
            // during early bootstrap — the matcher route would clobber it anyway).
            if (newUrl === currentPath) return;
            this.location.replaceState(newUrl);
        });

        // Effect: Sync language
        effect(() => {
            const lang = this.userLanguage();
            if (lang) {
                this.translate.use(lang);
            }
        });
    }

    // === Lifecycle ===
    public ngOnInit(): void {
        this.setTitleIfNotProd();

        // Skip authentication for anonymous PDF routes (used by Playwright PDF generation and public sharing)
        if (window.location.pathname.startsWith("/pdf")) {
            this.isPrintRoute.set(true);
            return;
        }

        this.store.dispatch(AuthenticationActions.initializeAuthentication());
    }

    // === Public Methods ===
    public onDrawerVisibleChange(visible: boolean): void {
        if (!visible) {
            this.store.dispatch(NavigationActions.toggleSettingsDrawer());
        }
    }

    // === Private Methods ===
    private setTitleIfNotProd(): void {
        if (environment.environmentName !== "Production") {
            this.titleService.setTitle(`Yllw Core - ${environment.environmentName}`);
        }
    }
}
